import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiRefreshCw, FiAlertTriangle, FiDollarSign, FiX, FiBook, FiCalendar,
  FiFileText, FiClipboard, FiBarChart2, FiGlobe, FiTag, FiDownloadCloud,
  FiClock, FiFlag, FiCreditCard, FiUploadCloud, FiTrendingUp, FiKey,
  FiHome, FiUnlock, FiLink, FiPercent, FiRepeat, FiList, FiEdit3, FiPieChart, FiShuffle,
} from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const NAV_CARDS = [
  { to: '/finance/quick-entry', icon: FiEdit3, label: 'Quick Entry', desc: 'Guided transaction entry for the CFO' },
  { to: '/finance/transfer-cash', icon: FiShuffle, label: 'Transfer Cash', desc: 'Move cash between holders' },
  { to: '/finance/cfo-dashboard', icon: FiPieChart, label: 'CFO Metrics', desc: 'CAC, LTV, ROI, cash position, cost of time' },
  { to: '/finance/accounts', icon: FiBook, label: 'Chart of Accounts', desc: 'Accounts per legal entity' },
  { to: '/finance/periods', icon: FiCalendar, label: 'Fiscal Periods', desc: 'Open, close, and reconcile periods' },
  { to: '/finance/entries', icon: FiFileText, label: 'Journal Entries', desc: 'Post, review, and reverse entries' },
  { to: '/finance/expenses', icon: FiClipboard, label: 'Expenses', desc: 'Submit and approve expenses' },
  { to: '/finance/reports', icon: FiBarChart2, label: 'Reports', desc: 'Trial balance and financial statements' },
  { to: '/finance/sales-invoices', icon: FiTag, label: 'Sales Invoices', desc: 'e-Fatura/e-Arşiv-shaped invoicing' },
  { to: '/finance/intercompany', icon: FiGlobe, label: 'Intercompany', desc: 'İzmir ↔ Zimbabwe, dual sign-off' },
  { to: '/finance/auditor-exports', icon: FiDownloadCloud, label: 'Auditor Exports', desc: 'Watermarked, time-boxed PDF packages' },
  { to: '/finance/tax-deadlines', icon: FiClock, label: 'Tax Deadlines', desc: 'Türkiye and Zimbabwe tax calendar' },
  { to: '/finance/anomaly-flags', icon: FiFlag, label: 'Anomaly Flags', desc: 'Review-only rule-based signals' },
  { to: '/finance/payment-providers', icon: FiCreditCard, label: 'Payment Providers', desc: 'Provider account mappings, payouts' },
  { to: '/finance/excel-migrations', icon: FiUploadCloud, label: 'Excel Migrations', desc: 'Review and sign off migration variance' },
  { to: '/finance/fx-rates', icon: FiTrendingUp, label: 'FX Rates', desc: 'Exchange rate history for cross-currency entries' },
  { to: '/finance/access-grants', icon: FiKey, label: 'Access Grants', desc: 'Scoped, time-boxed finance access for staff' },
  { to: '/finance/bank-accounts', icon: FiHome, label: 'Bank Accounts', desc: 'Company bank accounts and statement import' },
  { to: '/finance/bank-statement-lines', icon: FiLink, label: 'Bank Statement Lines', desc: 'Match imported lines to journal entries' },
  { to: '/finance/period-reopen-requests', icon: FiUnlock, label: 'Period Reopen Requests', desc: 'Dual sign-off to reopen a locked period' },
  { to: '/finance/zimbabwe-tax', icon: FiPercent, label: 'Zimbabwe Tax (QPD)', desc: 'Annual estimate and quarterly payment tracking' },
  { to: '/finance/recurring-templates', icon: FiRepeat, label: 'Recurring Templates', desc: 'Auto-generate recurring journal entries' },
  { to: '/finance/audit-log', icon: FiList, label: 'Audit Log', desc: 'Every high-risk finance action, Chairman/FD-only' },
];

const FinanceDashboard = () => {
  const [entities, setEntities] = useState([]);
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [openPeriods, setOpenPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, expRes, perRes] = await Promise.all([
        financeAPI.entities.list(),
        financeAPI.expenses.list({ status: 'SUBMITTED' }),
        financeAPI.periods.list({ state: 'OPEN' }),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setPendingExpenses(expRes.data.results || expRes.data || []);
      setOpenPeriods(perRes.data.results || perRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load the finance dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiDollarSign size={22} />Finance</h1>
          <p style={fs.pageSubtitle}>Ledger-grade financial management — every number here is derived live from posted journal entries.</p>
        </div>
        <button onClick={load} title="Refresh" style={fs.iconButton}>
          <FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        <div style={fs.statCard}>
          <div style={{ color: fs.colors.amber, fontSize: 26, fontWeight: 700 }}>{loading ? '—' : pendingExpenses.length}</div>
          <div style={{ color: fs.colors.textFaint, fontSize: 12 }}>Expenses awaiting approval</div>
        </div>
        <div style={fs.statCard}>
          <div style={{ color: fs.colors.blue, fontSize: 26, fontWeight: 700 }}>{loading ? '—' : openPeriods.length}</div>
          <div style={{ color: fs.colors.textFaint, fontSize: 12 }}>Open fiscal periods</div>
        </div>
        <div style={fs.statCard}>
          <div style={{ color: fs.colors.textBright, fontSize: 26, fontWeight: 700 }}>{loading ? '—' : entities.length}</div>
          <div style={{ color: fs.colors.textFaint, fontSize: 12 }}>Legal entities</div>
        </div>
      </div>

      {entities.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ ...fs.label, marginBottom: 10 }}><FiGlobe size={12} style={{ marginRight: 5, verticalAlign: -1 }} />Entities</div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {entities.map((e) => (
              <div key={e.id} style={{ ...fs.card, minWidth: 220 }}>
                <div style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{e.name}</div>
                <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>
                  {e.jurisdiction === 'TR' ? 'Türkiye' : e.jurisdiction === 'ZW' ? 'Zimbabwe' : e.jurisdiction} · {e.functional_currency}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ ...fs.label, marginBottom: 10 }}>Go to</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
        {NAV_CARDS.map(({ to, icon: Icon, label, desc }) => (
          <Link key={to} to={to} style={{ textDecoration: 'none' }}>
            <div style={{ ...fs.card, height: '100%', transition: 'border-color 0.15s' }}>
              <Icon size={20} color={fs.colors.purple} style={{ marginBottom: 10 }} />
              <div style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{label}</div>
              <div style={{ color: fs.colors.textFaint, fontSize: 12 }}>{desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default FinanceDashboard;
